import { useMutation, useQuery } from '@tanstack/react-query';
import type { MutationFunction, QueryFunction, QueryKey, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreEnumsPointOfSale,
  BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationResponse,
  BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest,
  BrokerNerdsBarkerCoreModelsInventoryAddListingRequest,
  BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest,
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryEventComment,
  BrokerNerdsBarkerCoreModelsInventoryEventCommentReceipt,
  BrokerNerdsBarkerCoreModelsInventoryEventMapping,
  BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess,
  BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponse,
  BrokerNerdsBarkerCoreModelsInventoryInventorySnapshot,
  BrokerNerdsBarkerCoreModelsInventoryInventorySnapshotNearTerm,
  BrokerNerdsBarkerCoreModelsInventoryListing,
  BrokerNerdsBarkerCoreModelsInventoryListingChange,
  BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest,
  BrokerNerdsBarkerCoreModelsInventoryPurchaseLive,
  BrokerNerdsBarkerCoreModelsInventorySeasonLocation,
  BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest,
  BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem,
  BrokerNerdsBarkerCoreModelsInventoryVendorLive,
  GetApiInventoryEventsParams,
  GetApiInventoryListingParams,
  GetApiInventoryParams,
  GetApiInventoryPurchasesExternalLookupParams,
  GetApiInventoryVendorsParams,
  PostApiInventoryEventsEventIdCommentsParams,
  PutApiInventoryEventsEventIdCommentsCommentIdParams,
  SystemTextJsonPatchJsonPatchDocument,
} from './models';

export const getApiInventoryStats = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.get(`/api/Inventory/Stats`, options);
};

export const getGetApiInventoryStatsQueryKey = () => {
  return [`/api/Inventory/Stats`] as const;
};

export const getGetApiInventoryStatsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryStats>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStats>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryStatsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryStats>>> = ({ signal }) => getApiInventoryStats({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStats>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryStats>>>;
export type GetApiInventoryStatsQueryError = AxiosError<void>;

export const useGetApiInventoryStats = <TData = Awaited<ReturnType<typeof getApiInventoryStats>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStats>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiInventoryValidate = (
  brokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsAdministrativeValidateIntegrationResponse>> => {
  return axios.put(`/api/Inventory/Validate`, brokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest, options);
};

export const getPutApiInventoryValidateMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryValidate>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryValidate>>, TError, { data: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryValidate>>, { data: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return putApiInventoryValidate(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryValidateMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryValidate>>>;
export type PutApiInventoryValidateMutationBody = BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest;
export type PutApiInventoryValidateMutationError = AxiosError<void>;

export const usePutApiInventoryValidate = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryValidate>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryValidate>>, TError, { data: BrokerNerdsBarkerCoreModelsAdministrativeValidatePointOfSaleRequest }, TContext> => {
  const mutationOptions = getPutApiInventoryValidateMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventory = (params: GetApiInventoryParams, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponse>> => {
  return axios.get(`/api/Inventory`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiInventoryQueryKey = (params: GetApiInventoryParams) => {
  return [`/api/Inventory`, ...(params ? [params] : [])] as const;
};

export const getGetApiInventoryQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventory>>, TError = AxiosError<void>>(
  params: GetApiInventoryParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventory>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventory>>> = ({ signal }) => getApiInventory(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventory>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventory>>>;
export type GetApiInventoryQueryError = AxiosError<void>;

export const useGetApiInventory = <TData = Awaited<ReturnType<typeof getApiInventory>>, TError = AxiosError<void>>(
  params: GetApiInventoryParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventory>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryEvents = (params: GetApiInventoryEventsParams, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEvent[]>> => {
  return axios.get(`/api/Inventory/Events`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiInventoryEventsQueryKey = (params: GetApiInventoryEventsParams) => {
  return [`/api/Inventory/Events`, ...(params ? [params] : [])] as const;
};

export const getGetApiInventoryEventsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEvents>>, TError = AxiosError<void>>(
  params: GetApiInventoryEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEvents>>> = ({ signal }) => getApiInventoryEvents(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEvents>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryEventsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEvents>>>;
export type GetApiInventoryEventsQueryError = AxiosError<void>;

export const useGetApiInventoryEvents = <TData = Awaited<ReturnType<typeof getApiInventoryEvents>>, TError = AxiosError<void>>(
  params: GetApiInventoryEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryEventsEventId = (eventId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEvent>> => {
  return axios.get(`/api/Inventory/Events/${eventId}`, options);
};

export const getGetApiInventoryEventsEventIdQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}`] as const;
};

export const getGetApiInventoryEventsEventIdQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventId>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventId>>> = ({ signal }) => getApiInventoryEventsEventId(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventId>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventId>>>;
export type GetApiInventoryEventsEventIdQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventId = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventId>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const patchApiInventoryEventsEventId = (
  eventId: string,
  systemTextJsonPatchJsonPatchDocument: SystemTextJsonPatchJsonPatchDocument,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEvent>> => {
  return axios.patch(`/api/Inventory/Events/${eventId}`, systemTextJsonPatchJsonPatchDocument, options);
};

export const getPatchApiInventoryEventsEventIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>, TError, { eventId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>, TError, { eventId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>, { eventId: string; data: SystemTextJsonPatchJsonPatchDocument }> = (props) => {
    const { eventId, data } = props ?? {};

    return patchApiInventoryEventsEventId(eventId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchApiInventoryEventsEventIdMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>>;
export type PatchApiInventoryEventsEventIdMutationBody = SystemTextJsonPatchJsonPatchDocument;
export type PatchApiInventoryEventsEventIdMutationError = AxiosError<void>;

export const usePatchApiInventoryEventsEventId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>, TError, { eventId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof patchApiInventoryEventsEventId>>, TError, { eventId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext> => {
  const mutationOptions = getPatchApiInventoryEventsEventIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryEventsEventIdListings = (eventId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing[]>> => {
  return axios.get(`/api/Inventory/Events/${eventId}/Listings`, options);
};

export const getGetApiInventoryEventsEventIdListingsQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}/Listings`] as const;
};

export const getGetApiInventoryEventsEventIdListingsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdListingsQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>> = ({ signal }) =>
    getApiInventoryEventsEventIdListings(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdListingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>>;
export type GetApiInventoryEventsEventIdListingsQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventIdListings = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdListings>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdListingsQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryEventsEventIdMappings = (eventId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventMapping[]>> => {
  return axios.get(`/api/Inventory/Events/${eventId}/Mappings`, options);
};

export const getGetApiInventoryEventsEventIdMappingsQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}/Mappings`] as const;
};

export const getGetApiInventoryEventsEventIdMappingsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdMappingsQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>> = ({ signal }) =>
    getApiInventoryEventsEventIdMappings(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdMappingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>>;
export type GetApiInventoryEventsEventIdMappingsQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventIdMappings = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdMappings>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdMappingsQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postApiInventoryEventsEventIdMappings = (
  eventId: string,
  brokerNerdsBarkerCoreModelsInventoryEventMappingSuccess: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventMapping>> => {
  return axios.post(`/api/Inventory/Events/${eventId}/Mappings`, brokerNerdsBarkerCoreModelsInventoryEventMappingSuccess, options);
};

export const getPostApiInventoryEventsEventIdMappingsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>,
    TError,
    { eventId: string; data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>,
  TError,
  { eventId: string; data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>,
    { eventId: string; data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess }
  > = (props) => {
    const { eventId, data } = props ?? {};

    return postApiInventoryEventsEventIdMappings(eventId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryEventsEventIdMappingsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>>;
export type PostApiInventoryEventsEventIdMappingsMutationBody = BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess;
export type PostApiInventoryEventsEventIdMappingsMutationError = AxiosError<void>;

export const usePostApiInventoryEventsEventIdMappings = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>,
    TError,
    { eventId: string; data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiInventoryEventsEventIdMappings>>,
  TError,
  { eventId: string; data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess },
  TContext
> => {
  const mutationOptions = getPostApiInventoryEventsEventIdMappingsMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Get event mappings for a list of event IDs
 */
export const postApiInventoryEventsMappings = (
  postApiInventoryEventsMappingsBody: string[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventMapping[]>> => {
  return axios.post(`/api/Inventory/Events/Mappings`, postApiInventoryEventsMappingsBody, options);
};

export const getPostApiInventoryEventsMappingsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>, TError, { data: string[] }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>, TError, { data: string[] }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>, { data: string[] }> = (props) => {
    const { data } = props ?? {};

    return postApiInventoryEventsMappings(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryEventsMappingsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>>;
export type PostApiInventoryEventsMappingsMutationBody = string[];
export type PostApiInventoryEventsMappingsMutationError = AxiosError<void>;

/**
 * @summary Get event mappings for a list of event IDs
 */
export const usePostApiInventoryEventsMappings = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>, TError, { data: string[] }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventoryEventsMappings>>, TError, { data: string[] }, TContext> => {
  const mutationOptions = getPostApiInventoryEventsMappingsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const deleteApiInventoryEventsEventIdMappingsMarketplaceId = (
  eventId: string,
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.delete(`/api/Inventory/Events/${eventId}/Mappings/${marketplaceId}`, options);
};

export const getDeleteApiInventoryEventsEventIdMappingsMarketplaceIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>,
    TError,
    { eventId: string; marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>,
  TError,
  { eventId: string; marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>,
    { eventId: string; marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace }
  > = (props) => {
    const { eventId, marketplaceId } = props ?? {};

    return deleteApiInventoryEventsEventIdMappingsMarketplaceId(eventId, marketplaceId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApiInventoryEventsEventIdMappingsMarketplaceIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>>;

export type DeleteApiInventoryEventsEventIdMappingsMarketplaceIdMutationError = AxiosError<void>;

export const useDeleteApiInventoryEventsEventIdMappingsMarketplaceId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>,
    TError,
    { eventId: string; marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdMappingsMarketplaceId>>,
  TError,
  { eventId: string; marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace },
  TContext
> => {
  const mutationOptions = getDeleteApiInventoryEventsEventIdMappingsMarketplaceIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putApiInventoryEventsEventIdViewed = (eventId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.put(`/api/Inventory/Events/${eventId}/Viewed`, undefined, options);
};

export const getPutApiInventoryEventsEventIdViewedMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>, TError, { eventId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>, TError, { eventId: string }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>, { eventId: string }> = (props) => {
    const { eventId } = props ?? {};

    return putApiInventoryEventsEventIdViewed(eventId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryEventsEventIdViewedMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>>;

export type PutApiInventoryEventsEventIdViewedMutationError = AxiosError<void>;

export const usePutApiInventoryEventsEventIdViewed = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>, TError, { eventId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryEventsEventIdViewed>>, TError, { eventId: string }, TContext> => {
  const mutationOptions = getPutApiInventoryEventsEventIdViewedMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiInventoryListings = (
  brokerNerdsBarkerCoreModelsInventoryAddListingRequest: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.post(`/api/Inventory/Listings`, brokerNerdsBarkerCoreModelsInventoryAddListingRequest, options);
};

export const getPostApiInventoryListingsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListings>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListings>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventoryListings>>, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest }> = (props) => {
    const { data } = props ?? {};

    return postApiInventoryListings(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryListingsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryListings>>>;
export type PostApiInventoryListingsMutationBody = BrokerNerdsBarkerCoreModelsInventoryAddListingRequest;
export type PostApiInventoryListingsMutationError = AxiosError<void>;

export const usePostApiInventoryListings = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListings>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventoryListings>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest }, TContext> => {
  const mutationOptions = getPostApiInventoryListingsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiInventoryListingsBulk = (
  brokerNerdsBarkerCoreModelsInventoryAddListingRequest: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing[]>> => {
  return axios.post(`/api/Inventory/Listings/Bulk`, brokerNerdsBarkerCoreModelsInventoryAddListingRequest, options);
};

export const getPostApiInventoryListingsBulkMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[] }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[] }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[] }> = (props) => {
    const { data } = props ?? {};

    return postApiInventoryListingsBulk(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryListingsBulkMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>>;
export type PostApiInventoryListingsBulkMutationBody = BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[];
export type PostApiInventoryListingsBulkMutationError = AxiosError<void>;

export const usePostApiInventoryListingsBulk = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[] }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventoryListingsBulk>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddListingRequest[] }, TContext> => {
  const mutationOptions = getPostApiInventoryListingsBulkMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putApiInventoryListingsBulk = (
  brokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.put(`/api/Inventory/Listings/Bulk`, brokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem, options);
};

export const getPutApiInventoryListingsBulkMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryListingsBulk>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiInventoryListingsBulk>>,
  TError,
  { data: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[] },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryListingsBulk>>, { data: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[] }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return putApiInventoryListingsBulk(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryListingsBulkMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryListingsBulk>>>;
export type PutApiInventoryListingsBulkMutationBody = BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[];
export type PutApiInventoryListingsBulkMutationError = AxiosError<void>;

export const usePutApiInventoryListingsBulk = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryListingsBulk>>,
    TError,
    { data: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[] },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryListingsBulk>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryUpdateListingsRequestBulkItem[] }, TContext> => {
  const mutationOptions = getPutApiInventoryListingsBulkMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryListingsListingId = (listingId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.get(`/api/Inventory/Listings/${listingId}`, options);
};

export const getGetApiInventoryListingsListingIdQueryKey = (listingId: string) => {
  return [`/api/Inventory/Listings/${listingId}`] as const;
};

export const getGetApiInventoryListingsListingIdQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryListingsListingId>>, TError = AxiosError<void>>(
  listingId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryListingsListingIdQueryKey(listingId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryListingsListingId>>> = ({ signal }) =>
    getApiInventoryListingsListingId(listingId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!listingId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingId>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryListingsListingIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryListingsListingId>>>;
export type GetApiInventoryListingsListingIdQueryError = AxiosError<void>;

export const useGetApiInventoryListingsListingId = <TData = Awaited<ReturnType<typeof getApiInventoryListingsListingId>>, TError = AxiosError<void>>(
  listingId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryListingsListingIdQueryOptions(listingId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const patchApiInventoryListingsListingId = (
  listingId: string,
  systemTextJsonPatchJsonPatchDocument: SystemTextJsonPatchJsonPatchDocument,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.patch(`/api/Inventory/Listings/${listingId}`, systemTextJsonPatchJsonPatchDocument, options);
};

export const getPatchApiInventoryListingsListingIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>,
    TError,
    { listingId: string; data: SystemTextJsonPatchJsonPatchDocument },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>, TError, { listingId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>, { listingId: string; data: SystemTextJsonPatchJsonPatchDocument }> = (
    props,
  ) => {
    const { listingId, data } = props ?? {};

    return patchApiInventoryListingsListingId(listingId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchApiInventoryListingsListingIdMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>>;
export type PatchApiInventoryListingsListingIdMutationBody = SystemTextJsonPatchJsonPatchDocument;
export type PatchApiInventoryListingsListingIdMutationError = AxiosError<void>;

export const usePatchApiInventoryListingsListingId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>,
    TError,
    { listingId: string; data: SystemTextJsonPatchJsonPatchDocument },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof patchApiInventoryListingsListingId>>, TError, { listingId: string; data: SystemTextJsonPatchJsonPatchDocument }, TContext> => {
  const mutationOptions = getPatchApiInventoryListingsListingIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const deleteApiInventoryListingsListingId = (listingId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.delete(`/api/Inventory/Listings/${listingId}`, options);
};

export const getDeleteApiInventoryListingsListingIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>, TError, { listingId: string }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>, { listingId: string }> = (props) => {
    const { listingId } = props ?? {};

    return deleteApiInventoryListingsListingId(listingId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApiInventoryListingsListingIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>>;

export type DeleteApiInventoryListingsListingIdMutationError = AxiosError<void>;

export const useDeleteApiInventoryListingsListingId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof deleteApiInventoryListingsListingId>>, TError, { listingId: string }, TContext> => {
  const mutationOptions = getDeleteApiInventoryListingsListingIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryListing = (
  params?: GetApiInventoryListingParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.get(`/api/Inventory/Listing`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiInventoryListingQueryKey = (params?: GetApiInventoryListingParams) => {
  return [`/api/Inventory/Listing`, ...(params ? [params] : [])] as const;
};

export const getGetApiInventoryListingQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryListing>>, TError = AxiosError<void>>(
  params?: GetApiInventoryListingParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListing>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryListingQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryListing>>> = ({ signal }) => getApiInventoryListing(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListing>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryListingQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryListing>>>;
export type GetApiInventoryListingQueryError = AxiosError<void>;

export const useGetApiInventoryListing = <TData = Awaited<ReturnType<typeof getApiInventoryListing>>, TError = AxiosError<void>>(
  params?: GetApiInventoryListingParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListing>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryListingQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Places a listing on hold indefinitely
 */
export const putApiInventoryListingListingIdReserve = (listingId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.put(`/api/Inventory/Listing/${listingId}/Reserve`, undefined, options);
};

export const getPutApiInventoryListingListingIdReserveMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>, TError, { listingId: string }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>, { listingId: string }> = (props) => {
    const { listingId } = props ?? {};

    return putApiInventoryListingListingIdReserve(listingId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryListingListingIdReserveMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>>;

export type PutApiInventoryListingListingIdReserveMutationError = AxiosError<void>;

/**
 * @summary Places a listing on hold indefinitely
 */
export const usePutApiInventoryListingListingIdReserve = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryListingListingIdReserve>>, TError, { listingId: string }, TContext> => {
  const mutationOptions = getPutApiInventoryListingListingIdReserveMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Removes a listing from hold
 */
export const putApiInventoryListingListingIdUnreserve = (listingId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.put(`/api/Inventory/Listing/${listingId}/Unreserve`, undefined, options);
};

export const getPutApiInventoryListingListingIdUnreserveMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>, TError, { listingId: string }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>, { listingId: string }> = (props) => {
    const { listingId } = props ?? {};

    return putApiInventoryListingListingIdUnreserve(listingId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryListingListingIdUnreserveMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>>;

export type PutApiInventoryListingListingIdUnreserveMutationError = AxiosError<void>;

/**
 * @summary Removes a listing from hold
 */
export const usePutApiInventoryListingListingIdUnreserve = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryListingListingIdUnreserve>>, TError, { listingId: string }, TContext> => {
  const mutationOptions = getPutApiInventoryListingListingIdUnreserveMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putApiInventoryListingsListingIdViewed = (listingId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.put(`/api/Inventory/Listings/${listingId}/Viewed`, undefined, options);
};

export const getPutApiInventoryListingsListingIdViewedMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>, TError, { listingId: string }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>, { listingId: string }> = (props) => {
    const { listingId } = props ?? {};

    return putApiInventoryListingsListingIdViewed(listingId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryListingsListingIdViewedMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>>;

export type PutApiInventoryListingsListingIdViewedMutationError = AxiosError<void>;

export const usePutApiInventoryListingsListingIdViewed = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>, TError, { listingId: string }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof putApiInventoryListingsListingIdViewed>>, TError, { listingId: string }, TContext> => {
  const mutationOptions = getPutApiInventoryListingsListingIdViewedMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryListingsListingIdChanges = (
  listingId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListingChange[]>> => {
  return axios.get(`/api/Inventory/Listings/${listingId}/Changes`, options);
};

export const getGetApiInventoryListingsListingIdChangesQueryKey = (listingId: string) => {
  return [`/api/Inventory/Listings/${listingId}/Changes`] as const;
};

export const getGetApiInventoryListingsListingIdChangesQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>, TError = AxiosError<void>>(
  listingId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryListingsListingIdChangesQueryKey(listingId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>> = ({ signal }) =>
    getApiInventoryListingsListingIdChanges(listingId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!listingId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryListingsListingIdChangesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>>;
export type GetApiInventoryListingsListingIdChangesQueryError = AxiosError<void>;

export const useGetApiInventoryListingsListingIdChanges = <TData = Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>, TError = AxiosError<void>>(
  listingId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryListingsListingIdChanges>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryListingsListingIdChangesQueryOptions(listingId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiInventoryListingsListingIdSplit = (
  listingId: string,
  brokerNerdsBarkerCoreModelsInventorySplitInventoryRequest: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing[]>> => {
  return axios.put(`/api/Inventory/Listings/${listingId}/Split`, brokerNerdsBarkerCoreModelsInventorySplitInventoryRequest, options);
};

export const getPutApiInventoryListingsListingIdSplitMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>,
    TError,
    { listingId: string; data: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>,
  TError,
  { listingId: string; data: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>,
    { listingId: string; data: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest }
  > = (props) => {
    const { listingId, data } = props ?? {};

    return putApiInventoryListingsListingIdSplit(listingId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryListingsListingIdSplitMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>>;
export type PutApiInventoryListingsListingIdSplitMutationBody = BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest;
export type PutApiInventoryListingsListingIdSplitMutationError = AxiosError<void>;

export const usePutApiInventoryListingsListingIdSplit = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>,
    TError,
    { listingId: string; data: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiInventoryListingsListingIdSplit>>,
  TError,
  { listingId: string; data: BrokerNerdsBarkerCoreModelsInventorySplitInventoryRequest },
  TContext
> => {
  const mutationOptions = getPutApiInventoryListingsListingIdSplitMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryTags = (options?: AxiosRequestConfig): Promise<AxiosResponse<string[]>> => {
  return axios.get(`/api/Inventory/Tags`, options);
};

export const getGetApiInventoryTagsQueryKey = () => {
  return [`/api/Inventory/Tags`] as const;
};

export const getGetApiInventoryTagsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryTags>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryTags>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryTagsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryTags>>> = ({ signal }) => getApiInventoryTags({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryTags>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryTagsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryTags>>>;
export type GetApiInventoryTagsQueryError = AxiosError<void>;

export const useGetApiInventoryTags = <TData = Awaited<ReturnType<typeof getApiInventoryTags>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryTags>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryTagsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postApiInventoryEventsEventIdComments = (
  eventId: string,
  params?: PostApiInventoryEventsEventIdCommentsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventComment>> => {
  return axios.post(`/api/Inventory/Events/${eventId}/Comments`, undefined, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getPostApiInventoryEventsEventIdCommentsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>,
    TError,
    { eventId: string; params?: PostApiInventoryEventsEventIdCommentsParams },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>,
  TError,
  { eventId: string; params?: PostApiInventoryEventsEventIdCommentsParams },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>,
    { eventId: string; params?: PostApiInventoryEventsEventIdCommentsParams }
  > = (props) => {
    const { eventId, params } = props ?? {};

    return postApiInventoryEventsEventIdComments(eventId, params, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryEventsEventIdCommentsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>>;

export type PostApiInventoryEventsEventIdCommentsMutationError = AxiosError<void>;

export const usePostApiInventoryEventsEventIdComments = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>,
    TError,
    { eventId: string; params?: PostApiInventoryEventsEventIdCommentsParams },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof postApiInventoryEventsEventIdComments>>,
  TError,
  { eventId: string; params?: PostApiInventoryEventsEventIdCommentsParams },
  TContext
> => {
  const mutationOptions = getPostApiInventoryEventsEventIdCommentsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryEventsEventIdComments = (eventId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventComment[]>> => {
  return axios.get(`/api/Inventory/Events/${eventId}/Comments`, options);
};

export const getGetApiInventoryEventsEventIdCommentsQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}/Comments`] as const;
};

export const getGetApiInventoryEventsEventIdCommentsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdCommentsQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>> = ({ signal }) =>
    getApiInventoryEventsEventIdComments(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdCommentsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>>;
export type GetApiInventoryEventsEventIdCommentsQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventIdComments = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdComments>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdCommentsQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryEventsCommentsReceipts = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventCommentReceipt[]>> => {
  return axios.get(`/api/Inventory/Events/Comments/Receipts`, options);
};

export const getGetApiInventoryEventsCommentsReceiptsQueryKey = () => {
  return [`/api/Inventory/Events/Comments/Receipts`] as const;
};

export const getGetApiInventoryEventsCommentsReceiptsQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>,
  TError = AxiosError<void>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsCommentsReceiptsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>> = ({ signal }) =>
    getApiInventoryEventsCommentsReceipts({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryEventsCommentsReceiptsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>>;
export type GetApiInventoryEventsCommentsReceiptsQueryError = AxiosError<void>;

export const useGetApiInventoryEventsCommentsReceipts = <TData = Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsCommentsReceipts>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsCommentsReceiptsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiInventoryEventsEventIdCommentsCommentId = (
  eventId: string,
  commentId: number,
  params?: PutApiInventoryEventsEventIdCommentsCommentIdParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventComment>> => {
  return axios.put(`/api/Inventory/Events/${eventId}/Comments/${commentId}`, undefined, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getPutApiInventoryEventsEventIdCommentsCommentIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>,
    TError,
    { eventId: string; commentId: number; params?: PutApiInventoryEventsEventIdCommentsCommentIdParams },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>,
  TError,
  { eventId: string; commentId: number; params?: PutApiInventoryEventsEventIdCommentsCommentIdParams },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>,
    { eventId: string; commentId: number; params?: PutApiInventoryEventsEventIdCommentsCommentIdParams }
  > = (props) => {
    const { eventId, commentId, params } = props ?? {};

    return putApiInventoryEventsEventIdCommentsCommentId(eventId, commentId, params, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiInventoryEventsEventIdCommentsCommentIdMutationResult = NonNullable<Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>>;

export type PutApiInventoryEventsEventIdCommentsCommentIdMutationError = AxiosError<void>;

export const usePutApiInventoryEventsEventIdCommentsCommentId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>,
    TError,
    { eventId: string; commentId: number; params?: PutApiInventoryEventsEventIdCommentsCommentIdParams },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiInventoryEventsEventIdCommentsCommentId>>,
  TError,
  { eventId: string; commentId: number; params?: PutApiInventoryEventsEventIdCommentsCommentIdParams },
  TContext
> => {
  const mutationOptions = getPutApiInventoryEventsEventIdCommentsCommentIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const deleteApiInventoryEventsEventIdCommentsCommentId = (eventId: string, commentId: number, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.delete(`/api/Inventory/Events/${eventId}/Comments/${commentId}`, options);
};

export const getDeleteApiInventoryEventsEventIdCommentsCommentIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>, TError, { eventId: string; commentId: number }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>, TError, { eventId: string; commentId: number }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>, { eventId: string; commentId: number }> = (props) => {
    const { eventId, commentId } = props ?? {};

    return deleteApiInventoryEventsEventIdCommentsCommentId(eventId, commentId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApiInventoryEventsEventIdCommentsCommentIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>>;

export type DeleteApiInventoryEventsEventIdCommentsCommentIdMutationError = AxiosError<void>;

export const useDeleteApiInventoryEventsEventIdCommentsCommentId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>, TError, { eventId: string; commentId: number }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof deleteApiInventoryEventsEventIdCommentsCommentId>>, TError, { eventId: string; commentId: number }, TContext> => {
  const mutationOptions = getDeleteApiInventoryEventsEventIdCommentsCommentIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiInventorySnapshotsAll = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Inventory/Snapshots/All`, undefined, options);
};

export const getPostApiInventorySnapshotsAllMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>, TError, void, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>, void> = () => {
    return postApiInventorySnapshotsAll(axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventorySnapshotsAllMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>>;

export type PostApiInventorySnapshotsAllMutationError = AxiosError<void>;

export const usePostApiInventorySnapshotsAll = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventorySnapshotsAll>>, TError, void, TContext> => {
  const mutationOptions = getPostApiInventorySnapshotsAllMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postApiInventorySnapshotsNearTerm = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
  return axios.post(`/api/Inventory/Snapshots/NearTerm`, undefined, options);
};

export const getPostApiInventorySnapshotsNearTermMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>, TError, void, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>, void> = () => {
    return postApiInventorySnapshotsNearTerm(axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventorySnapshotsNearTermMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>>;

export type PostApiInventorySnapshotsNearTermMutationError = AxiosError<void>;

export const usePostApiInventorySnapshotsNearTerm = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>, TError, void, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventorySnapshotsNearTerm>>, TError, void, TContext> => {
  const mutationOptions = getPostApiInventorySnapshotsNearTermMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryEventsEventIdSnapshots = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryInventorySnapshot[]>> => {
  return axios.get(`/api/Inventory/Events/${eventId}/Snapshots`, options);
};

export const getGetApiInventoryEventsEventIdSnapshotsQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}/Snapshots`] as const;
};

export const getGetApiInventoryEventsEventIdSnapshotsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdSnapshotsQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>> = ({ signal }) =>
    getApiInventoryEventsEventIdSnapshots(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdSnapshotsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>>;
export type GetApiInventoryEventsEventIdSnapshotsQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventIdSnapshots = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshots>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdSnapshotsQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryEventsEventIdSnapshotsNearTerm = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryInventorySnapshotNearTerm[]>> => {
  return axios.get(`/api/Inventory/Events/${eventId}/Snapshots/NearTerm`, options);
};

export const getGetApiInventoryEventsEventIdSnapshotsNearTermQueryKey = (eventId: string) => {
  return [`/api/Inventory/Events/${eventId}/Snapshots/NearTerm`] as const;
};

export const getGetApiInventoryEventsEventIdSnapshotsNearTermQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>,
  TError = AxiosError<void>,
>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryEventsEventIdSnapshotsNearTermQueryKey(eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>> = ({ signal }) =>
    getApiInventoryEventsEventIdSnapshotsNearTerm(eventId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!eventId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryEventsEventIdSnapshotsNearTermQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>>;
export type GetApiInventoryEventsEventIdSnapshotsNearTermQueryError = AxiosError<void>;

export const useGetApiInventoryEventsEventIdSnapshotsNearTerm = <TData = Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>, TError = AxiosError<void>>(
  eventId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryEventsEventIdSnapshotsNearTerm>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryEventsEventIdSnapshotsNearTermQueryOptions(eventId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postApiInventoryListingsMerge = (
  brokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryListing>> => {
  return axios.post(`/api/Inventory/Listings/Merge`, brokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest, options);
};

export const getPostApiInventoryListingsMergeMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>, { data: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest }> = (props) => {
    const { data } = props ?? {};

    return postApiInventoryListingsMerge(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryListingsMergeMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>>;
export type PostApiInventoryListingsMergeMutationBody = BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest;
export type PostApiInventoryListingsMergeMutationError = AxiosError<void>;

export const usePostApiInventoryListingsMerge = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventoryListingsMerge>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryMergeInventoryRequest }, TContext> => {
  const mutationOptions = getPostApiInventoryListingsMergeMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventorySeasons = (options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventorySeasonLocation[]>> => {
  return axios.get(`/api/Inventory/Seasons`, options);
};

export const getGetApiInventorySeasonsQueryKey = () => {
  return [`/api/Inventory/Seasons`] as const;
};

export const getGetApiInventorySeasonsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventorySeasons>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventorySeasons>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventorySeasonsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventorySeasons>>> = ({ signal }) => getApiInventorySeasons({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventorySeasons>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventorySeasonsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventorySeasons>>>;
export type GetApiInventorySeasonsQueryError = AxiosError<void>;

export const useGetApiInventorySeasons = <TData = Awaited<ReturnType<typeof getApiInventorySeasons>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventorySeasons>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventorySeasonsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryPurchasesExternalLookup = (
  params: GetApiInventoryPurchasesExternalLookupParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryPurchaseLive>> => {
  return axios.get(`/api/Inventory/Purchases/ExternalLookup`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiInventoryPurchasesExternalLookupQueryKey = (params: GetApiInventoryPurchasesExternalLookupParams) => {
  return [`/api/Inventory/Purchases/ExternalLookup`, ...(params ? [params] : [])] as const;
};

export const getGetApiInventoryPurchasesExternalLookupQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>, TError = AxiosError<void>>(
  params: GetApiInventoryPurchasesExternalLookupParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryPurchasesExternalLookupQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>> = ({ signal }) =>
    getApiInventoryPurchasesExternalLookup(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryPurchasesExternalLookupQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>>;
export type GetApiInventoryPurchasesExternalLookupQueryError = AxiosError<void>;

export const useGetApiInventoryPurchasesExternalLookup = <TData = Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>, TError = AxiosError<void>>(
  params: GetApiInventoryPurchasesExternalLookupParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryPurchasesExternalLookup>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryPurchasesExternalLookupQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryVendors = (
  params?: GetApiInventoryVendorsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryVendorLive[]>> => {
  return axios.get(`/api/Inventory/Vendors`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiInventoryVendorsQueryKey = (params?: GetApiInventoryVendorsParams) => {
  return [`/api/Inventory/Vendors`, ...(params ? [params] : [])] as const;
};

export const getGetApiInventoryVendorsQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryVendors>>, TError = AxiosError<void>>(
  params?: GetApiInventoryVendorsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryVendors>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryVendorsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryVendors>>> = ({ signal }) => getApiInventoryVendors(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryVendors>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryVendorsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryVendors>>>;
export type GetApiInventoryVendorsQueryError = AxiosError<void>;

export const useGetApiInventoryVendors = <TData = Awaited<ReturnType<typeof getApiInventoryVendors>>, TError = AxiosError<void>>(
  params?: GetApiInventoryVendorsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryVendors>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryVendorsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postApiInventoryVendors = (
  brokerNerdsBarkerCoreModelsInventoryAddVendorRequest: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryVendorLive>> => {
  return axios.post(`/api/Inventory/Vendors`, brokerNerdsBarkerCoreModelsInventoryAddVendorRequest, options);
};

export const getPostApiInventoryVendorsMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryVendors>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryVendors>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest }, TContext> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInventoryVendors>>, { data: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest }> = (props) => {
    const { data } = props ?? {};

    return postApiInventoryVendors(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiInventoryVendorsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInventoryVendors>>>;
export type PostApiInventoryVendorsMutationBody = BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest;
export type PostApiInventoryVendorsMutationError = AxiosError<void>;

export const usePostApiInventoryVendors = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof postApiInventoryVendors>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest }, TContext>;
  axios?: AxiosRequestConfig;
}): UseMutationResult<Awaited<ReturnType<typeof postApiInventoryVendors>>, TError, { data: BrokerNerdsBarkerCoreModelsInventoryAddVendorRequest }, TContext> => {
  const mutationOptions = getPostApiInventoryVendorsMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getApiInventoryStatus = (options?: AxiosRequestConfig): Promise<AxiosResponse<string>> => {
  return axios.get(`/api/Inventory/Status`, options);
};

export const getGetApiInventoryStatusQueryKey = () => {
  return [`/api/Inventory/Status`] as const;
};

export const getGetApiInventoryStatusQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryStatus>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatus>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryStatus>>> = ({ signal }) => getApiInventoryStatus({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatus>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiInventoryStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryStatus>>>;
export type GetApiInventoryStatusQueryError = AxiosError<void>;

export const useGetApiInventoryStatus = <TData = Awaited<ReturnType<typeof getApiInventoryStatus>>, TError = AxiosError<void>>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatus>>, TError, TData>>;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryStatusQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryStatusPointOfSaleId = (pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale, options?: AxiosRequestConfig): Promise<AxiosResponse<string>> => {
  return axios.get(`/api/Inventory/Status/${pointOfSaleId}`, options);
};

export const getGetApiInventoryStatusPointOfSaleIdQueryKey = (pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale) => {
  return [`/api/Inventory/Status/${pointOfSaleId}`] as const;
};

export const getGetApiInventoryStatusPointOfSaleIdQueryOptions = <TData = Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>, TError = AxiosError<unknown>>(
  pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryStatusPointOfSaleIdQueryKey(pointOfSaleId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>> = ({ signal }) =>
    getApiInventoryStatusPointOfSaleId(pointOfSaleId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!pointOfSaleId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiInventoryStatusPointOfSaleIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>>;
export type GetApiInventoryStatusPointOfSaleIdQueryError = AxiosError<unknown>;

export const useGetApiInventoryStatusPointOfSaleId = <TData = Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>, TError = AxiosError<unknown>>(
  pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryStatusPointOfSaleId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryStatusPointOfSaleIdQueryOptions(pointOfSaleId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getApiInventoryPosPriceChangeStatusPointOfSaleId = (
  pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<string>> => {
  return axios.get(`/api/Inventory/PosPriceChange/Status/${pointOfSaleId}`, options);
};

export const getGetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryKey = (pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale) => {
  return [`/api/Inventory/PosPriceChange/Status/${pointOfSaleId}`] as const;
};

export const getGetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>,
  TError = AxiosError<unknown>,
>(
  pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryKey(pointOfSaleId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>> = ({ signal }) =>
    getApiInventoryPosPriceChangeStatusPointOfSaleId(pointOfSaleId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!pointOfSaleId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>>;
export type GetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryError = AxiosError<unknown>;

export const useGetApiInventoryPosPriceChangeStatusPointOfSaleId = <
  TData = Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>,
  TError = AxiosError<unknown>,
>(
  pointOfSaleId: BrokerNerdsBarkerCoreEnumsPointOfSale,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiInventoryPosPriceChangeStatusPointOfSaleId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiInventoryPosPriceChangeStatusPointOfSaleIdQueryOptions(pointOfSaleId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
